import { TEST_IDS } from '@va/constants';
import { FilterIconV2 } from '@va/icons';
import { useTranslate } from '@va/localization';
import { Button } from '@va/ui/design-system';
import { DropdownArrow } from '@va/util/components';
import { isWixMiniApp } from '@va/util/helpers';
import { DashboardAppWorker } from '@va/util/misc';
import classNames from 'classnames';
import { useCallback, useRef, useState } from 'react';
import { AddFilterTooltip } from './add-filter-tooltip/AddFilterTooltip';
import { FILTER_SELECTION_MODAL_CLASS_IDENTIFIER } from './constants';
import { useFiltersContext } from './filters-context';

type AddFilterButtonProps = {
  className?: string;
  size?: 'small' | 'large';
  isResponsive?: boolean;
  /**
   * If true, clicking always opens the filters dropdown instead of toggling the inline applied-filters
   * subheader. Use when the host RB doesn't render an `<AppliedFilters />` subheader.
   */
  alwaysOpenDropdown?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
};

export const AddFilterButton = ({
  className,
  size = 'large',
  isResponsive = true,
  alwaysOpenDropdown = false,
  disabled = false,
  disabledTooltip,
}: AddFilterButtonProps) => {
  const translate = useTranslate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { appliedFilters, isExpanded: isExpandedCtx, toggleAppliedFiltersVisibility } = useFiltersContext();
  const isExpanded = !alwaysOpenDropdown && isExpandedCtx;

  const filtersExist = appliedFilters.length > 0;
  const filterColor = filtersExist ? '#FFFFFF' : '#3C3C3C';

  const handleButtonClick = useCallback(() => {
    if (disabled) return;

    if (filtersExist && !alwaysOpenDropdown) {
      toggleAppliedFiltersVisibility();
      return;
    }

    setIsDropdownOpen((prev) => !prev);
  }, [alwaysOpenDropdown, disabled, filtersExist, toggleAppliedFiltersVisibility]);

  return (
    <AddFilterTooltip
      isOpen={isDropdownOpen}
      openChange={(isOpen) => {
        if (!isOpen) buttonRef.current?.focus();

        setIsDropdownOpen(isOpen);
      }}
    >
      <div
        className={classNames(className, 'inline-block')}
        onClick={() => {
          if (isWixMiniApp()) {
            DashboardAppWorker.trackEvent({
              category: 'Recording Main View',
              action: 'onclick',
              label: 'Add/Edit Filter',
              value: 'Button',
            });
          }
        }}
        onMouseEnter={() => {
          if (isWixMiniApp()) {
            DashboardAppWorker.trackEvent({
              category: 'Recording Main View',
              action: 'hover',
              label: 'Add/Edit Filter',
              value: 'Button',
            });
          }
        }}
      >
        <Button
          ref={buttonRef}
          color={filtersExist ? 'primary' : 'tertiary'}
          icon={() => (
            <FilterIconV2
              className={classNames({
                'w-15px h-15px': size === 'small',
                'w-18px h-18px': size === 'large',
                'sm:w-15px sm:h-15px': size === 'large' && isResponsive,
              })}
              color={filterColor}
            />
          )}
          bottomIcon={
            <DropdownArrow
              iconClassName={classNames({
                'w-1.5': size === 'small',
                'w-2.5': size === 'large',
                'sm:w-1.5': size === 'large' && isResponsive,
              })}
              color={filtersExist ? '#FFFFFF80' : filterColor}
              open={!disabled && (isDropdownOpen || (filtersExist && isExpanded))}
            />
          }
          data-testid={TEST_IDS.generic.filters.toggleBtn}
          onClick={handleButtonClick}
          className={classNames({
            'sm:w-9 sm:h-9 sm:p-9px sm:rounded-9px': isResponsive,
            'bg-green-haze!': filtersExist && (disabled || !(isExpanded || isDropdownOpen)),
            'bg-black-lighter-13!': !filtersExist && isDropdownOpen,
            'cursor-not-allowed': disabled,
          })}
          bottomIconWrapperCss={classNames({ 'sm:bottom-0.5': isResponsive })}
          tooltip={disabled ? disabledTooltip : translate('tooltip.label.applyFilters')}
          tooltipProps={disabled ? { trigger: 'hover' } : { disabled: filtersExist }}
          size={size}
        />
      </div>
    </AddFilterTooltip>
  );
};

export function isClickInsideFilterModal(e: Event) {
  const filterSelectionModal = document.querySelector(`.${FILTER_SELECTION_MODAL_CLASS_IDENTIFIER}`);
  if (filterSelectionModal && filterSelectionModal.contains(e.target as any)) return true;
  return false;
}
